import {
  Typography,
  type TypographyPropsVariantOverrides,
} from '@mui/material';
import type { OverridableStringUnion } from '@mui/types';
import i18next from 'i18next';
import {
  type CSSProperties,
  type ElementType,
  useEffect,
  useReducer,
} from 'react';

import type { TranslationVariables } from '../../models/translations';

interface FormatLabelProps {
  translationKey: string;
  variables?: TranslationVariables;
  typographyVariant?:
    | OverridableStringUnion<'inherit', TypographyPropsVariantOverrides>
    | undefined;
  style?: CSSProperties;
  className?: string;
  dataTestId?: string;
  component?: ElementType;
}

// NOTE(i18n): `TranslatedLabel` + `useI18nextRerender` here are mirrored by a
// near-verbatim local copy in fleet-management
// (`apps/fleet-management/src/common/components/TranslatedLabel/TranslatedLabel.tsx`).
// Keep the two in sync until that app collapses its copy into a re-export barrel.

/**
 * Re-renders the caller on i18next `languageChanged` events. i18next.t is not
 * reactive on its own, and i18next emits no event for interpolation-default
 * changes (those are surfaced by re-emitting `languageChanged`), so we subscribe
 * to the shared singleton directly rather than depending on react-i18next context.
 */
const useI18nextRerender = (): void => {
  const [, rerender] = useReducer((count: number) => count + 1, 0);
  useEffect(() => {
    i18next.on('languageChanged', rerender);
    return () => {
      i18next.off('languageChanged', rerender);
    };
  }, []);
};

const TranslatedLabel = ({
  translationKey,
  variables,
  typographyVariant,
  style,
  className,
  dataTestId,
  component = 'span',
}: FormatLabelProps) => {
  useI18nextRerender();
  return (
    <Typography
      variant={typographyVariant}
      className={className}
      style={style}
      data-testid={dataTestId}
      component={component}
    >
      {i18next.t<string>(translationKey, variables)}
    </Typography>
  );
};

export default TranslatedLabel;
